'use client';

import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { usePathname } from 'next/navigation';

import {
  classes,
  CSSTransitionProperties
} from '@/next-utils/css-utils/scss-utils';
import { EventType } from '@/services/isomorphic/UserInteractionService';

import { NavigationVM } from '@/react/view-models/NavigationVM';
import { withEcommerceEnabled } from '@/react/components/utility/EcommerceEnabled';
import { Icon, IconTypes } from '../../../../../core-ui/Icon';
import { Link } from '../../../../../core-ui/Link';
import { HoverIntent } from '../../../../../utility/HoverIntent';
import { IDesktopNavbarProps } from './IDesktopNavbarProps';
import { Dropdown } from './components/Dropdown';

import S from './styles.base.module.scss';
/**
 * A constant specifying the amount of time that must pass to prove intent to hover
 * over an item.
 */
const timeoutForHoverIntent = 175;

const DESKTOP_TRANSITION_PROPERTIES = [
  CSSTransitionProperties.Opacity,
  CSSTransitionProperties.Top
];

/**
 * The Desktop navbar (Mega Nav) component. Provides dropdowns
 * and everything.
 *
 * Is NOT responsive. Must be used with Mobile component.
 */
export const Desktop: FunctionComponent<IDesktopNavbarProps> =
  withEcommerceEnabled(function Desktop({
    showDropdownArrows = false,
    dropdownVisibleOn = 'hover',
    categoryClassName,
    activeCategoryClassName,
    dropdownTotalAnimationLengthMS = 750,
    dropdownSectionAnimationLengthMS = 450,
    transitionProperties = DESKTOP_TRANSITION_PROPERTIES,
    navigationItems
  }) {
    const pathname = usePathname();
    const listRef = useRef<HTMLUListElement>(null);

    const [visible, setVisible] = useState<string | null>(null);

    const [navigationVM] = useState(() => new NavigationVM(navigationItems));

    // The hide callback can be used by all nodes, since it doesn't change
    const doHide = useCallback(() => {
      setVisible(null);
    }, []);

    // When we navigate to another page, close the desktop dropdown menu.
    useEffect(() => {
      doHide();
    }, [doHide, pathname]);

    // If in 'click' mode, hide the dropdown when clicked outside
    useEffect(() => {
      if (dropdownVisibleOn !== 'click') {
        return () => {};
      }

      const onDocumentClick = (e: MouseEvent): void => {
        if (!listRef.current?.contains(e.target as HTMLElement)) {
          doHide();
        }
      };

      document.body.addEventListener('click', onDocumentClick);
      return () => {
        document.body.removeEventListener('click', onDocumentClick);
      };
    }, [dropdownVisibleOn, doHide]);

    const visibleListItemClassNames = [
      S.navItemDropdownVisible,
      activeCategoryClassName
    ];

    return (
      <ul className={S.nav} role="menu" ref={listRef}>
        {navigationVM.navigationModels?.map((navItem) => {
          const { id } = navItem;
          const hasDropdown =
            navItem.categories && navItem.categories.length > 0;

          const doShow = (): void => {
            setVisible(id);
          };

          const doToggle = (): void => setVisible(id === visible ? null : id);

          return (
            <HoverIntent
              key={id}
              disabled={dropdownVisibleOn !== 'hover'}
              delay={timeoutForHoverIntent}
              onHoverIn={doShow}
              onHoverOut={doHide}
              // Favor the specific tab implementation here.
              tabIndex={-1}
              activatedByFocus={false}
              deactivatedByBlur={false}
            >
              <li
                tabIndex={hasDropdown ? 0 : -1}
                role="menuitem"
                className={classes(
                  S.navItem,
                  categoryClassName,
                  visible === id && visibleListItemClassNames
                )}
                aria-haspopup={hasDropdown}
                aria-expanded={hasDropdown ? visible === id : undefined}
                onKeyDown={(event): void => {
                  if (event.key === ' ' || event.key === 'Enter') {
                    doToggle();

                    event.preventDefault();
                  }

                  if (event.key === 'Escape') {
                    doHide();
                  }
                }}
              >
                <Link
                  className={S.navLink}
                  href={navItem.href}
                  {...(dropdownVisibleOn === 'click'
                    ? {
                        onClick: (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          doToggle();
                        }
                      }
                    : {})}
                  interactionDetails={{
                    action: EventType.NavigationLink,
                    link: {
                      linkName: navItem.name.toLowerCase().replace(/\s+/g, '-'),
                      linkCategory: 'top-navigation',
                      linkSection: navItem.name.toLowerCase().replace(/\s+/g, '-')
                    }
                  }}
                >
                  {navItem.name}
                  {hasDropdown && showDropdownArrows && (
                    <span className={S.dropdownIcon}>
                      <Icon icon={IconTypes.AngleDown} />
                    </span>
                  )}
                </Link>

                {hasDropdown && (
                  <Dropdown
                    category={navItem}
                    visibleCategoryID={visible}
                    totalAnimationTimeMS={dropdownTotalAnimationLengthMS}
                    sectionTransitionMS={dropdownSectionAnimationLengthMS}
                    transitionProperties={transitionProperties}
                  />
                )}
              </li>
            </HoverIntent>
          );
        })}
      </ul>
    );
  });
